import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Upload, X, Package, ShoppingBag, Clock, Users, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Dresses", "Tops", "Outerwear", "Bottoms", "Accessories"];
const EMPTY = { name: "", description: "", price: "", cost: "", category: "Dresses", colors: [], size_stock: {}, images: [], featured: false };
const DISCOUNT_EMPTY = { code: "", type: "percent", value: "", expires_at: "", usage_limit: "" };
const STATUSES = ["pending", "confirmed", "shipped", "cancelled"];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sizeInput, setSizeInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [dform, setDform] = useState(DISCOUNT_EMPTY);
  const [accounting, setAccounting] = useState({});
  const [adInput, setAdInput] = useState("");

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    api.get("/admin/accounting").then((r) => { setAccounting(r.data); setAdInput(String(r.data.advertising_cost ?? 0)); }).catch(() => {});
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (user === null) return <div className="min-h-[70vh]" />;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ name: p.name, description: p.description || "", price: String(p.price), cost: String(p.cost ?? 0), category: p.category, colors: p.colors || [], size_stock: p.size_stock || {}, images: p.images || [], featured: !!p.featured }); setOpen(true); };

  const addTag = (type) => {
    const val = (type === "sizes" ? sizeInput : colorInput).trim();
    if (!val) return;
    setForm((f) => ({ ...f, [type]: [...f[type], val] }));
    type === "sizes" ? setSizeInput("") : setColorInput("");
  };
  const removeTag = (type, i) => setForm((f) => ({ ...f, [type]: f[type].filter((_, idx) => idx !== i) }));

  const addSize = () => {
    const val = sizeInput.trim();
    if (!val) return;
    setForm((f) => ({ ...f, size_stock: { ...f.size_stock, [val]: f.size_stock[val] ?? 0 } }));
    setSizeInput("");
  };
  const setSizeQty = (s, q) => setForm((f) => ({ ...f, size_stock: { ...f.size_stock, [s]: Math.max(0, parseInt(q) || 0) } }));
  const removeSize = (s) => setForm((f) => { const ss = { ...f.size_stock }; delete ss[s]; return { ...f, size_stock: ss }; });

  const saveDiscount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/discounts", {
        code: dform.code, type: dform.type, value: parseFloat(dform.value) || 0,
        expires_at: dform.expires_at || null,
        usage_limit: dform.usage_limit ? parseInt(dform.usage_limit) : null,
      });
      toast.success("Discount code created"); setDform(DISCOUNT_EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create code"); }
  };
  const removeDiscount = async (id) => { await api.delete(`/discounts/${id}`); toast.success("Code deleted"); load(); };

  const saveAdvertising = async () => {
    await api.put("/admin/advertising", null, { params: { amount: parseFloat(adInput) || 0 } });
    toast.success("Advertising cost updated"); load();
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const size_stock = Object.fromEntries(Object.entries(form.size_stock).map(([k, v]) => [k, parseInt(v) || 0]));
    const payload = { ...form, price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0, size_stock };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load();
  };

  const setStatus = async (oid, status) => {
    await api.put(`/admin/orders/${oid}`, null, { params: { status } });
    toast.success("Order updated"); load();
  };

  const statCards = [
    { label: "Products", value: stats.products, icon: Package },
    { label: "Orders", value: stats.orders, icon: ShoppingBag },
    { label: "Pending", value: stats.pending, icon: Clock },
    { label: "Customers", value: stats.customers, icon: Users },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-12 min-h-[80vh]">
      <p className="label-caps text-muted-foreground mb-2">Dashboard</p>
      <h1 className="font-serif-display text-5xl font-light tracking-tighter mb-10">Atelier control</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line mb-10">
        {statCards.map((s) => (
          <div key={s.label} className="bg-pearl p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon strokeWidth={1.5} className="w-5 h-5 text-muted-foreground mb-4" />
            <p className="font-serif-display text-4xl">{s.value ?? 0}</p>
            <p className="label-caps text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-8 border-b border-line mb-8">
        {["products", "orders", "discounts", "accounting"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`label-caps pb-3 -mb-px border-b-2 transition-colors ${tab === t ? "border-ink text-ink" : "border-transparent text-muted-foreground hover:text-ink"}`}>{t}</button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-6">
            <button onClick={openNew} className="inline-flex items-center gap-2 bg-ink text-pearl px-5 py-3 label-caps hover:bg-wine transition-colors" data-testid="add-product-btn"><Plus strokeWidth={1.5} className="w-4 h-4" /> Add product</button>
          </div>
          <div className="border border-line divide-y divide-line" data-testid="admin-products">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                <div className="w-14 h-16 bg-surface overflow-hidden shrink-0">{p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="img-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif-display text-lg truncate">{p.name} {p.featured && <span className="label-caps text-wine">· Featured</span>}</p>
                  <p className="label-caps text-muted-foreground">{p.category} · ${p.price}{p.cost ? ` · cost $${p.cost}` : ""} · Stock {p.stock}</p>
                </div>
                <button onClick={() => openEdit(p)} className="w-9 h-9 flex items-center justify-center border border-line hover:border-ink transition-colors" data-testid={`edit-${p.id}`}><Pencil strokeWidth={1.5} className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="w-9 h-9 flex items-center justify-center border border-line hover:border-destructive hover:text-destructive transition-colors" data-testid={`delete-${p.id}`}><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-line divide-y divide-line" data-testid="admin-orders">
          {orders.length === 0 ? <p className="p-8 text-muted-foreground">No orders yet.</p> : orders.map((o) => (
            <div key={o.id} className="p-5" data-testid={`admin-order-${o.id}`}>
              <div className="flex flex-wrap justify-between items-center gap-3">
                <div>
                  <p className="font-serif-display text-xl">{o.full_name} · ${o.total?.toFixed(0)}</p>
                  <p className="label-caps text-muted-foreground mt-1">{o.phone}{o.user_email ? ` · ${o.user_email}` : ""} · {new Date(o.created_at).toLocaleDateString()}</p>
                  <p className="text-sm text-muted-foreground mt-1">{o.address}, {o.city}</p>
                </div>
                <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                  <SelectTrigger className="w-40 rounded-none" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">{o.items?.map((it) => `${it.name} (${[it.color, it.size].filter(Boolean).join("/")}) ×${it.quantity}`).join("  ·  ")}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "discounts" && (
        <div className="grid lg:grid-cols-2 gap-10" data-testid="discounts-panel">
          <form onSubmit={saveDiscount} className="space-y-5 max-w-md" data-testid="discount-form">
            <h2 className="font-serif-display text-2xl">Create a code</h2>
            <div><label className="label-caps text-muted-foreground">Code</label><input required value={dform.code} onChange={(e) => setDform({ ...dform, code: e.target.value.toUpperCase() })} data-testid="df-code" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" placeholder="WELCOME10" /></div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="label-caps text-muted-foreground">Type</label>
                <Select value={dform.type} onValueChange={(v) => setDform({ ...dform, type: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="df-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percentage %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="label-caps text-muted-foreground">Value</label><input required type="number" step="0.01" value={dform.value} onChange={(e) => setDform({ ...dform, value: e.target.value })} data-testid="df-value" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div><label className="label-caps text-muted-foreground">Expires (optional)</label><input type="date" value={dform.expires_at} onChange={(e) => setDform({ ...dform, expires_at: e.target.value })} data-testid="df-expires" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
              <div><label className="label-caps text-muted-foreground">Usage limit (optional)</label><input type="number" value={dform.usage_limit} onChange={(e) => setDform({ ...dform, usage_limit: e.target.value })} data-testid="df-limit" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
            </div>
            <button type="submit" className="w-full bg-ink text-pearl py-3 label-caps hover:bg-wine transition-colors" data-testid="df-save">Create code</button>
          </form>
          <div className="border border-line divide-y divide-line self-start" data-testid="discount-list">
            {discounts.length === 0 ? <p className="p-6 text-muted-foreground">No codes yet.</p> : discounts.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-4" data-testid={`discount-${d.id}`}>
                <Ticket strokeWidth={1.5} className="w-5 h-5 text-wine shrink-0" />
                <div className="flex-1">
                  <p className="font-serif-display text-xl">{d.code}</p>
                  <p className="label-caps text-muted-foreground">{d.type === "percent" ? `${d.value}% off` : `$${d.value} off`}{d.expires_at ? ` · exp ${d.expires_at}` : ""}{d.usage_limit ? ` · ${d.used_count}/${d.usage_limit} used` : ` · ${d.used_count} used`}</p>
                </div>
                <button onClick={() => removeDiscount(d.id)} className="w-9 h-9 flex items-center justify-center border border-line hover:border-destructive hover:text-destructive transition-colors" data-testid={`delete-discount-${d.id}`}><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "accounting" && (
        <div data-testid="accounting-panel">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line mb-8">
            {[
              { label: "Revenue", value: accounting.revenue, prefix: "$" },
              { label: "Cost of goods", value: accounting.cogs, prefix: "$" },
              { label: "Gross profit", value: accounting.gross_profit, prefix: "$" },
              { label: "Units sold", value: accounting.units_sold, prefix: "" },
            ].map((s) => (
              <div key={s.label} className="bg-pearl p-6" data-testid={`acc-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                <p className="font-serif-display text-3xl">{s.prefix}{Number(s.value ?? 0).toLocaleString()}</p>
                <p className="label-caps text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="border border-line p-6">
              <label className="label-caps text-muted-foreground">Advertising cost ($)</label>
              <div className="flex gap-2 mt-2">
                <input type="number" step="0.01" value={adInput} onChange={(e) => setAdInput(e.target.value)} data-testid="ad-input" className="flex-1 border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" />
                <button onClick={saveAdvertising} data-testid="ad-save" className="px-5 bg-ink text-pearl label-caps hover:bg-wine transition-colors">Save</button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Net profit counts only orders marked <strong>confirmed</strong> or <strong>shipped</strong>.</p>
            </div>
            <div className="border border-ink p-8 bg-ink text-pearl" data-testid="acc-net-profit">
              <p className="label-caps text-pearl/50">Net profit</p>
              <p className="font-serif-display text-5xl mt-2">${Number(accounting.net_profit ?? 0).toLocaleString()}</p>
              <p className="text-pearl/50 text-xs mt-3">Gross ${Number(accounting.gross_profit ?? 0).toLocaleString()} − Ad spend ${Number(accounting.advertising_cost ?? 0).toLocaleString()} · {accounting.orders_count ?? 0} orders counted</p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-display text-3xl font-light">{editing ? "Edit product" : "New product"}</DialogTitle><DialogDescription className="label-caps text-muted-foreground">Fill in the details below</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-5 mt-2" data-testid="product-form">
            <div><label className="label-caps text-muted-foreground">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
            <div><label className="label-caps text-muted-foreground">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="pf-desc" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink resize-none text-base" /></div>
            <div className="grid grid-cols-2 gap-5">
              <div><label className="label-caps text-muted-foreground">Price ($)</label><input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
              <div><label className="label-caps text-muted-foreground">Cost ($)</label><input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="pf-cost" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" /></div>
            </div>
            <div>
              <label className="label-caps text-muted-foreground">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-caps text-muted-foreground">Sizes & stock</label>
              <div className="flex gap-2 mt-1">
                <input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSize(); } }} data-testid="pf-size-input" className="flex-1 border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" placeholder="Add size (e.g. S) & press Enter" />
                <button type="button" onClick={addSize} className="px-3 border border-line hover:border-ink"><Plus strokeWidth={1.5} className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2 mt-3">
                {Object.entries(form.size_stock).map(([s, q]) => (
                  <div key={s} className="flex items-center gap-3" data-testid={`pf-size-row-${s}`}>
                    <span className="w-14 label-caps">{s}</span>
                    <input type="number" min="0" value={q} onChange={(e) => setSizeQty(s, e.target.value)} data-testid={`pf-size-qty-${s}`} className="w-24 border-b border-line py-1 bg-transparent focus:outline-none focus:border-ink text-base" />
                    <span className="text-xs text-muted-foreground">in stock</span>
                    <button type="button" onClick={() => removeSize(s)} className="ml-auto text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {Object.keys(form.size_stock).length === 0 && <p className="text-xs text-muted-foreground">Add at least one size with its stock quantity.</p>}
              </div>
            </div>
            <div>
              <label className="label-caps text-muted-foreground">Colors</label>
              <div className="flex gap-2 mt-1">
                <input value={colorInput} onChange={(e) => setColorInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag("colors"); } }} data-testid="pf-colors-input" className="flex-1 border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" placeholder="Add color & press Enter" />
                <button type="button" onClick={() => addTag("colors")} className="px-3 border border-line hover:border-ink"><Plus strokeWidth={1.5} className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.colors.map((t, i) => <span key={i} className="inline-flex items-center gap-1 bg-surface px-2 py-1 text-sm">{t}<button type="button" onClick={() => removeTag("colors", i)}><X className="w-3 h-3" /></button></span>)}
              </div>
            </div>
            <div>
              <label className="label-caps text-muted-foreground">Images</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.images.map((im, i) => (
                  <div key={i} className="relative w-16 h-20 bg-surface overflow-hidden group">
                    <img src={fileUrl(im)} alt="" className="img-cover" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))} className="absolute top-0 right-0 bg-ink text-pearl w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="w-16 h-20 border border-dashed border-line flex flex-col items-center justify-center cursor-pointer hover:border-ink transition-colors text-muted-foreground" data-testid="pf-upload">
                  {uploading ? <span className="text-[10px]">...</span> : <Upload strokeWidth={1.5} className="w-4 h-4" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Or paste an image URL:</p>
              <input onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { e.preventDefault(); setForm((f) => ({ ...f, images: [...f.images, e.target.value.trim()] })); e.target.value = ""; } }} data-testid="pf-image-url" className="w-full border-b border-line py-2 bg-transparent focus:outline-none focus:border-ink text-base" placeholder="https://... & press Enter" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" /> <span className="label-caps">Feature on homepage</span></label>
            <button type="submit" disabled={saving} className="w-full bg-ink text-pearl py-3 label-caps hover:bg-wine transition-colors disabled:opacity-50" data-testid="pf-save">{saving ? "Saving..." : editing ? "Update product" : "Create product"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
